import { useState, useRef } from 'react';
import { Button, Card, Alert, Table, Badge, Text, Loader, Group, Menu, Progress, Paper } from '@mantine/core';
import { showSuccess, showError, showInfo } from '../../common/notifications';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';

const GET_ALL_PROJECTS = gql`
  query getAllProjects {
    projectManagement {
      allProjects(limit: 1000, archived: false) {
        _id
        name
        clientName
        status
        client: projectClient {
          name
        }
        integration {
          sourceId
        }
      }
    }
  }
`;

const MANUAL_PROJECT_SYNC = gql`
  mutation ManualProjectSync($projectId: ID!) {
    manualProjectSync(projectId: $projectId)
  }
`;

const SYNC_PROJECT_DELIVERABLES = gql`
  mutation SyncProjectDeliverables($projectId: ID!) {
    syncProjectDeliverables(projectId: $projectId)
  }
`;

const SYNC_PROJECT_RESOURCES = gql`
  mutation SyncProjectResources($projectId: ID!) {
    syncProjectResources(projectId: $projectId)
  }
`;

const SYNC_PROJECT_ONLY = gql`
  mutation SyncProjectOnly($projectId: ID!) {
    syncProjectOnly(projectId: $projectId)
  }
`;

interface Project {
  _id: string;
  name: string;
  clientName?: string;
  status: string;
  client?: {
    name: string;
  };
  integration?: {
    sourceId: string;
  };
}

type SyncType = 'full' | 'deliverables' | 'resources' | 'project-only';

interface SyncProgress {
  current: number;
  total: number;
  currentProject: string;
  successCount: number;
  failureCount: number;
}

const NetsuiteProjectSyncUtilities = () => {
  const [syncingAll, setSyncingAll] = useState<SyncType | null>(null);
  const [syncingProjects, setSyncingProjects] = useState<Record<string, SyncType | null>>({});
  const [syncProgress, setSyncProgress] = useState<SyncProgress | null>(null);
  const shouldStopSyncRef = useRef(false);
  
  // Fetch all projects
  const { loading, data, refetch, error } = useQuery(GET_ALL_PROJECTS, {
    fetchPolicy: 'network-only',
  });
  
  // Use the GraphQL mutations for syncing
  const [syncProjectFull] = useMutation(MANUAL_PROJECT_SYNC);
  const [syncProjectDeliverables] = useMutation(SYNC_PROJECT_DELIVERABLES);
  const [syncProjectResources] = useMutation(SYNC_PROJECT_RESOURCES);
  const [syncProjectOnly] = useMutation(SYNC_PROJECT_ONLY);
  
  const projects: Project[] = data?.projectManagement?.allProjects || [];
  
  // Log for debugging
  console.log('Query data:', data);
  console.log('Projects found:', projects.length);
  if (error) console.error('Query error:', error);
  
  const handleSyncSingleProject = async (projectId: string, projectName: string, syncType: SyncType) => {
    setSyncingProjects(prev => ({ ...prev, [projectId]: syncType }));
    
    try {
      let mutation;
      let syncTypeLabel = '';
      
      switch (syncType) {
        case 'full':
          mutation = syncProjectFull;
          syncTypeLabel = 'Full sync';
          break;
        case 'deliverables':
          mutation = syncProjectDeliverables;
          syncTypeLabel = 'Deliverables sync';
          break;
        case 'resources':
          mutation = syncProjectResources;
          syncTypeLabel = 'Resources sync';
          break;
        case 'project-only':
          mutation = syncProjectOnly;
          syncTypeLabel = 'Project details sync';
          break;
      }
      
      await mutation({
        variables: { projectId }
      });
      
      showSuccess({
        message: `${syncTypeLabel} completed for: ${projectName}`,
      });
      
      if (syncType === 'full') {
        await refetch();
      }
    } catch (error: any) {
      showError({
        message: `Failed to sync project ${projectName}: ${error.message}`,
      });
    } finally {
      setSyncingProjects(prev => ({ ...prev, [projectId]: null }));
    }
  };
  
  const handleSyncAllProjects = async (syncType: SyncType) => {
    setSyncingAll(syncType);
    shouldStopSyncRef.current = false; // Reset stop flag
    
    // Filter projects that are "In Progress" and have NetSuite integration
    const projectsToSync = projects.filter(p => 
      p.status === 'In Progress' && p.integration?.sourceId
    );
    
    if (projectsToSync.length === 0) {
      showInfo({
        message: 'No "In Progress" projects with NetSuite integration found',
      });
      setSyncingAll(null);
      return;
    }
    
    let syncTypeLabel = '';
    let mutation;
    
    switch (syncType) {
      case 'full':
        mutation = syncProjectFull;
        syncTypeLabel = 'Full sync';
        break;
      case 'deliverables':
        mutation = syncProjectDeliverables;
        syncTypeLabel = 'Deliverables sync';
        break;
      case 'resources':
        mutation = syncProjectResources;
        syncTypeLabel = 'Resources sync';
        break;
      case 'project-only':
        mutation = syncProjectOnly;
        syncTypeLabel = 'Project details sync';
        break;
    }
    
    // Initialize progress
    setSyncProgress({
      current: 0,
      total: projectsToSync.length,
      currentProject: '',
      successCount: 0,
      failureCount: 0
    });
    
    let successCount = 0;
    let failureCount = 0;
    let stoppedEarly = false;
    
    for (let i = 0; i < projectsToSync.length; i++) {
      // Check if user requested to stop
      if (shouldStopSyncRef.current) {
        stoppedEarly = true;
        showInfo({
          message: `Sync stopped by user. Processed ${i} of ${projectsToSync.length} projects.`,
        });
        break;
      }
      
      const project = projectsToSync[i];
      
      // Update progress
      setSyncProgress({
        current: i + 1,
        total: projectsToSync.length,
        currentProject: project.name,
        successCount,
        failureCount
      });
      
      try {
        await mutation({
          variables: { projectId: project._id }
        });
        successCount++;
      } catch (error) {
        failureCount++;
        console.error(`Error syncing project ${project.name}:`, error);
      }
    }
    
    // Final progress update
    if (!stoppedEarly) {
      setSyncProgress({
        current: projectsToSync.length,
        total: projectsToSync.length,
        currentProject: 'Complete',
        successCount,
        failureCount
      });
    }
    
    if (successCount > 0) {
      showSuccess({
        message: `${syncTypeLabel}: Successfully synced ${successCount} projects${stoppedEarly ? ' (stopped early)' : ''}`,
      });
    }
    
    if (failureCount > 0) {
      showError({
        message: `${failureCount} projects failed to sync`,
      });
    }
    
    if (syncType === 'full') {
      await refetch();
    }
    
    // Clear progress after a short delay
    setTimeout(() => {
      setSyncProgress(null);
      setSyncingAll(null);
      shouldStopSyncRef.current = false;
    }, 2000);
  };
  
  const handleStopSync = () => {
    shouldStopSyncRef.current = true;
    showInfo({
      message: 'Stopping sync after current project completes...',
    });
  };
  
  // Count projects with different criteria
  const inProgressCount = projects.filter(p => p.status === 'In Progress').length;
  const withNetSuiteCount = projects.filter(p => p.integration?.sourceId).length;
  const syncableCount = projects.filter(p => p.status === 'In Progress' && p.integration?.sourceId).length;
  
  const isSyncing = (projectId: string) => syncingProjects[projectId] !== null && syncingProjects[projectId] !== undefined;
  
  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Group position="apart" mb="md">
        <div>
          <Text size="xl" weight={700}>NetSuite Project Sync Utilities</Text>
          <Text size="sm" color="dimmed">
            Sync In Progress projects with NetSuite to get the latest data
          </Text>
        </div>
        <div style={{ textAlign: 'right' }}>
          <Badge color="blue" variant="filled" size="lg" style={{ marginBottom: '4px' }}>
            {projects.length} Total Projects
          </Badge>
          <br />
          <Text size="xs" color="dimmed">
            {inProgressCount} In Progress | {withNetSuiteCount} with NetSuite
          </Text>
        </div>
      </Group>
      
      <Alert 
        icon={<span>ℹ️</span>} 
        title="Sync Options" 
        color="blue"
        mb="md"
      >
        <Text size="sm">
          <strong>Full Sync:</strong> Updates project details, resources, and deliverables<br />
          <strong>Deliverables Only:</strong> Syncs only project deliverables/milestones<br />
          <strong>Resources Only:</strong> Syncs only project resources/team members<br />
          <strong>Project Details Only:</strong> Updates only basic project information
        </Text>
      </Alert>
      
      {syncProgress && (
        <Paper shadow="sm" p="md" mb="xl" withBorder>
          <Group position="apart" mb="xs">
            <Text size="sm" weight={600}>
              Sync Progress: {syncProgress.current} of {syncProgress.total} projects
            </Text>
            {syncProgress.currentProject !== 'Complete' && (
              <Button 
                size="xs" 
                color="red" 
                onClick={handleStopSync}
                leftIcon={<span>⏹</span>}
              >
                Stop Sync
              </Button>
            )}
          </Group>
          <Progress 
            value={(syncProgress.current / syncProgress.total) * 100} 
            size="xl" 
            radius="xl"
            mb="sm"
            animate
            color={syncProgress.currentProject === 'Complete' ? 'green' : 'blue'}
          />
          <Group position="apart">
            <Text size="xs" color="dimmed">
              {syncProgress.currentProject === 'Complete' ? (
                <strong>✓ Sync Complete</strong>
              ) : (
                <>Currently syncing: <strong>{syncProgress.currentProject}</strong></>
              )}
            </Text>
            <Group spacing="xs">
              <Badge color="green" variant="light">
                ✓ {syncProgress.successCount} Success
              </Badge>
              {syncProgress.failureCount > 0 && (
                <Badge color="red" variant="light">
                  ✗ {syncProgress.failureCount} Failed
                </Badge>
              )}
            </Group>
          </Group>
        </Paper>
      )}
      
      <Group mb="xl">
        <Button
          onClick={() => handleSyncAllProjects('full')}
          loading={syncingAll === 'full'}
          disabled={loading || syncableCount === 0 || syncingAll !== null}
          color="green"
        >
          Full Sync All ({syncableCount})
        </Button>
        <Button
          onClick={() => handleSyncAllProjects('deliverables')}
          loading={syncingAll === 'deliverables'}
          disabled={loading || syncableCount === 0 || syncingAll !== null}
          color="blue"
        >
          Sync All Deliverables
        </Button>
        <Button
          onClick={() => handleSyncAllProjects('resources')}
          loading={syncingAll === 'resources'}
          disabled={loading || syncableCount === 0 || syncingAll !== null}
          color="cyan"
        >
          Sync All Resources
        </Button>
        <Button
          onClick={() => handleSyncAllProjects('project-only')}
          loading={syncingAll === 'project-only'}
          disabled={loading || syncableCount === 0 || syncingAll !== null}
          color="gray"
        >
          Sync All Project Details
        </Button>
      </Group>
      
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Loader size="lg" />
        </div>
      ) : (
        <>
          {projects.length === 0 ? (
            <Alert color="yellow" mt="md">
              No projects found. Please check if you have the correct permissions.
            </Alert>
          ) : (
            <Table striped highlightOnHover>
              <thead>
                <tr>
                  <th>Project Name</th>
                  <th>Client</th>
                  <th>Status</th>
                  <th>NetSuite ID</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((project) => (
                  <tr key={project._id}>
                    <td>
                      <Text weight={500}>{project.name}</Text>
                    </td>
                    <td>{project.client?.name || project.clientName || 'No Client'}</td>
                    <td>
                      <Badge 
                        color={
                          project.status === 'In Progress' ? 'green' : 
                          project.status === 'Closed' ? 'gray' : 
                          project.status === 'Pending' ? 'orange' : 'blue'
                        } 
                        variant="light"
                      >
                        {project.status}
                      </Badge>
                    </td>
                    <td>
                      {project.integration?.sourceId ? (
                        <Badge color="green" variant="light">
                          {project.integration.sourceId}
                        </Badge>
                      ) : (
                        <Badge color="red" variant="light">
                          Not Linked
                        </Badge>
                      )}
                    </td>
                    <td>
                      {project.integration?.sourceId ? (
                        <Menu
                          disabled={syncingAll !== null || isSyncing(project._id)}
                          withinPortal
                        >
                          <Menu.Target>
                            <Button 
                              size="xs" 
                              loading={isSyncing(project._id)}
                              disabled={syncingAll !== null}
                            >
                              {syncingProjects[project._id] ? `Syncing ${syncingProjects[project._id]}...` : 'Sync Options'}
                            </Button>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item 
                              onClick={() => handleSyncSingleProject(project._id, project.name, 'full')}
                            >
                              Full Sync (All Data)
                            </Menu.Item>
                            <Menu.Divider />
                            <Menu.Item 
                              onClick={() => handleSyncSingleProject(project._id, project.name, 'deliverables')}
                            >
                              Sync Deliverables Only
                            </Menu.Item>
                            <Menu.Item 
                              onClick={() => handleSyncSingleProject(project._id, project.name, 'resources')}
                            >
                              Sync Resources Only
                            </Menu.Item>
                            <Menu.Item 
                              onClick={() => handleSyncSingleProject(project._id, project.name, 'project-only')}
                            >
                              Sync Project Details Only
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      ) : (
                        <Button size="xs" disabled>
                          Not Linked
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </>
      )}
    </Card>
  );
};

export default NetsuiteProjectSyncUtilities;